<template lang='pug'>
  .container(:class="theme")
    Transition(name='fade')
      .drawer-outer-container(
        v-if="internalShow"
        @mousedown="clickOverlay"
        @touchstart="clickOverlay"
        ref="overlayRef")
    Transition( name='slide')
      .drawer-container(
        v-if='internalShow'
        role="dialog"
        tabindex="0"
        ref="headerRef"
        :aria-label="__('Press escape to close')"
        @keydown.esc="hide()"
        :class='["drawer-"+position]'
        :style='{ maxWidth: width+"px" }'
      )
        .drawer-header
          slot(name='header')
            h3.title {{__(title)}}
          button.a11y.close(v-if="!preventClose && !hideCloseButton" @click='hide()' :aria-label="__('Close')")
            Icon(name='x')
        slot(name='top')
        .drawer-body
          slot(:hide="hide")
        .drawer-footer
          slot(name='footer')
        button.a11y(:aria-label="__('Close Sidebar')" @click="$emit('close')")
        button.a11y.a11y-tab-gaurd(@focus="headerRef?.focus()")
</template>

<script setup lang="ts">
import { computed, type ComputedRef, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { allowScroll, preventScroll } from '../../js/body-scroll'
import { useRogue } from '../../js/rogue'

interface DrawerConfig {
  drawerActive?: boolean
  drawerWidth?: number
}

const props = withDefaults(defineProps<{
  position?: 'left' | 'right',
  theme?: string | null,
  title?: string,
  force?: boolean,
  width?: number,
  preventClose?: boolean,
  onHide?: () => void,
  hideCloseButton?: boolean,
}>(), {
  position        : 'right',
  theme           : null,
  title           : '',
  force           : false,
  width           : 420,
  preventClose    : false,
  onHide          : undefined,
  hideCloseButton : false,
})

const emit = defineEmits<{
  close: []
  hide: []
}>()

const drawerActive = inject<(config: DrawerConfig) => void>('drawerActive', () => {})
const overlay = inject<ComputedRef<string | null>>('overlay', computed(() => null))
const hideOverlay = inject<() => void>('hideOverlay', () => {})

const rogue = useRogue()

const internalShow = ref(props.force)
const overlayRef = ref<HTMLElement | null>(null)
const headerRef = ref<HTMLElement | null>(null)

/**
 * On route path change we want to hide whatever is showing
 * in the drawer. If, for example, query parameters are only
 * changed, we don't want to hide.
 */
watch(() => rogue.router?.currentRoute.value?.path, (newPath, oldPath) => {
  if (newPath !== oldPath) {
    hide()
  }
})

onMounted(() => {
  preventScroll()
  const drawer: DrawerConfig = {
    drawerActive : true,
    drawerWidth  : props.width,
  }
  internalShow.value = true
  if (drawerActive) {
    drawerActive(drawer)
  }

  nextTick(() => {
    headerRef.value?.focus()
  })
})

onBeforeUnmount(() => {
  allowScroll()
  const skipLink = document.getElementById('skiplink')
  if (skipLink && skipLink instanceof HTMLElement) {
    skipLink.focus()
  }
})

function clickOverlay(e: MouseEvent | TouchEvent) {
  if (e.target === overlayRef.value) {
    hide()
  }
}

function hide() {
  if (props.preventClose) {
    return
  }

  internalShow.value = false
  setTimeout(() => {
    if (!props.onHide && overlay.value) {
      if (hideOverlay) {
        hideOverlay()
      }
    } else {
      emit('hide')
    }
  }, 200)
  if (drawerActive) {
    drawerActive({})
  }
}

defineExpose({
  hide,
})
</script>

<style lang='scss' scoped>
.slide-leave-active,
.slide-enter-active {
  transition: transform 300ms;
}

.fade-leave-active,
.fade-enter-active {
  transition: opacity 300ms;
}

.slide-leave-active,
.fade-leave-active {
  transition-duration: 200ms;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drawer-left {

  &.slide-enter-from,
  &.slide-leave-to {
    transform: translate(-100%, 0);
  }
}

.drawer-right {

  &.slide-enter-from,
  &.slide-leave-to {
    transform: translate(100%, 0);
  }
}

.drawer-outer-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba($color-black, .75);
  z-index: 10;
}

.drawer-container {
  z-index: 15;
  width: 100%;
  max-width: 420px;
  background-color: $color-white;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.drawer-left {
    right: auto;
    left: 0;
  }
}

.drawer-header {
  background-color: $color-gray-x-light;
  border-bottom: $border-width-thin solid $color-gray-light;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $space-s;
  position: relative;

  :deep(h3) {
    margin-bottom: 0;
  }

  .title{
    max-width: 95%;
  }

  .h-left {
    display: flex;
    align-items: center;

    >*:not(:last-child) {
      margin-right: $space-xxxs;
    }
  }

  .h-right {
    z-index: 1;
  }

  &.close-only {
    height: 0;
    padding-top: 0;
    padding-bottom: 0;

    .h-right {
      position: absolute;
      top: $space-s;
      right: $space-s;
    }
  }
}

.rb-default {
  .drawer-body {
    padding: $space-s;
  }
}

.mobile-menu {
  .drawer-header {
    background-color: $color-black;
  }

  .close {
    fill: $color-white;
  }
}

.close {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  padding: $space-s;
  display: flex;
  align-items: center;

  .icon {
    width: 16px;
    height: 16px;
  }
}

.drawer-body {
  overflow-y: auto;
  overflow-x: hidden;
  flex-grow: 1;
  position: relative;
}
</style>

<style lang="scss">
/* Dark mode styles for Drawer */
[data-theme='dark'] .drawer-container,
.dark-mode .drawer-container {
  background-color: $color-black;
  color: $color-white;
}

[data-theme='dark'] .drawer-header,
.dark-mode .drawer-header {
  background-color: $color-black-light;
  border-bottom-color: $color-gray-x-dark;

  h3 {
    color: $color-white;
  }
}

[data-theme='dark'] .close,
.dark-mode .close {
  fill: $color-white;
}
</style>
